import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { startViewAs } from "../lib/api";
import { Plus, Trash, PencilSimple, FloppyDisk, X, Handshake, ChartBar, UserCircleGear, Envelope, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const EMPTY = { name: "", pix_key: "", pix_type: "", pix_name: "", phone: "", note: "", commission_pct: "", handle: "" };
const EMPTY_SETTINGS = { pix_key: "", pix_type: "", pix_name: "", monthly_goal: "" };
const PIX_TYPES = [
  { v: "", label: "—" },
  { v: "cpf", label: "CPF" },
  { v: "cnpj", label: "CNPJ" },
  { v: "email", label: "E-mail" },
  { v: "telefone", label: "Telefone" },
  { v: "aleatoria", label: "Aleatória" },
];

export default function Contractors() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [savingSettings, setSavingSettings] = useState(false);
  const [accessFor, setAccessFor] = useState(null);
  const nav = useNavigate();
  const { user } = useAuth();
  const isMaster = !!user?.is_master;

  const load = useCallback(async () => {
    const [c, s] = await Promise.all([api.get("/contractors"), api.get("/settings")]);
    setItems(c.data);
    setSettings({
      pix_key: s.data?.pix_key || "",
      pix_type: s.data?.pix_type || "",
      pix_name: s.data?.pix_name || "",
      monthly_goal: s.data?.monthly_goal ?? "",
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const payload = { ...settings };
      Object.keys(payload).forEach(k => { if (payload[k] === "") payload[k] = null; });
      if (payload.monthly_goal != null) {
        const n = parseFloat(String(payload.monthly_goal).replace(",", "."));
        payload.monthly_goal = isNaN(n) ? null : n;
      }
      await api.put("/settings", payload);
      toast.success("Chave Pix da loja salva!");
    } catch { toast.error("Erro ao salvar chave Pix"); }
    setSavingSettings(false);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Informe o nome do contratante");
    const payload = { ...form };
    Object.keys(payload).forEach(k => { if (payload[k] === "") payload[k] = null; });
    payload.name = form.name.trim();
    if (payload.commission_pct !== null) {
      const c = parseFloat(String(payload.commission_pct).replace(",", "."));
      payload.commission_pct = isNaN(c) ? null : c;
    }
    try {
      if (editing) {
        await api.put(`/contractors/${editing}`, payload);
        toast.success("Contratante atualizado");
      } else {
        await api.post("/contractors", payload);
        toast.success("Contratante cadastrado");
      }
      setForm(EMPTY); setEditing(null); load();
    } catch { toast.error("Erro ao salvar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este contratante? Lives vinculadas ficarão sem contratante.")) return;
    await api.delete(`/contractors/${id}`);
    load();
  };

  const startEdit = (c) => {
    setEditing(c.id);
    setForm({
      name: c.name || "",
      pix_key: c.pix_key || "",
      pix_type: c.pix_type || "",
      pix_name: c.pix_name || "",
      phone: c.phone || "",
      note: c.note || "",
      handle: c.handle || "",
      commission_pct: c.commission_pct != null ? String(c.commission_pct).replace(".", ",") : "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div data-testid="contractors-page" className="space-y-8">
      <header>
        <p className="label-brutal">Parceiros / Terceirizados</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black">Contratantes</h1>
        <p className="text-brand-ink2 mt-2">Cadastre clientes que contratam suas lives (lives terceirizadas). A chave Pix agora é definida em cada live.</p>
      </header>

      {/* Store's own Pix */}
      <form onSubmit={saveSettings} className="card-brutal p-6 bg-brand-yellow">
        <div className="mb-3">
          <p className="label-brutal">Sua loja</p>
          <h2 className="font-heading text-2xl font-black">Chave Pix & Meta mensal</h2>
          <p className="text-sm text-brand-ink2">Pix usado nas cobranças de lives sem contratante. A meta é aplicada ao <strong>Faturamento Desapegue</strong> no gráfico do Dashboard.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="label-brutal">Tipo de chave</label>
            <select data-testid="store-pix-type" className="input-brutal" value={settings.pix_type} onChange={(e) => setSettings({ ...settings, pix_type: e.target.value })}>
              {PIX_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label-brutal">Chave Pix</label>
            <input data-testid="store-pix-key" className="input-brutal" value={settings.pix_key} onChange={(e) => setSettings({ ...settings, pix_key: e.target.value })} placeholder="Ex: 28885915000140" />
          </div>
          <div>
            <label className="label-brutal">Titular</label>
            <input data-testid="store-pix-name" className="input-brutal" value={settings.pix_name} onChange={(e) => setSettings({ ...settings, pix_name: e.target.value })} placeholder="Ex: Herida Caroline" />
          </div>
          <div>
            <label className="label-brutal" title="Meta mensal de Faturamento Desapegue">Meta mensal (R$)</label>
            <input data-testid="store-monthly-goal" type="number" step="0.01" min="0" className="input-brutal" value={settings.monthly_goal} onChange={(e) => setSettings({ ...settings, monthly_goal: e.target.value })} placeholder="Ex: 5000" />
          </div>
          <div className="flex items-end">
            <button data-testid="save-settings-btn" type="submit" disabled={savingSettings} className="btn-pink w-full h-[52px]">
              <FloppyDisk weight="bold" size={16} /> Salvar
            </button>
          </div>
        </div>
      </form>

      <form onSubmit={submit} className="card-brutal p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="label-brutal">Nome *</label>
          <input data-testid="contractor-name-input" required className="input-brutal" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ex: Dra Nádia" />
        </div>
        <div>
          <label className="label-brutal">Telefone</label>
          <input data-testid="contractor-phone" className="input-brutal" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        </div>
        <div>
          <label className="label-brutal">@ dela como cliente</label>
          <input data-testid="contractor-handle" className="input-brutal" value={form.handle} onChange={(e) => set("handle", e.target.value.replace(/\s/g, ""))} placeholder="@perfil (para a Caixinha)" />
        </div>
        <div>
          <label className="label-brutal">Observação</label>
          <input data-testid="contractor-note" className="input-brutal" value={form.note} onChange={(e) => set("note", e.target.value)} />
        </div>
        <div>
          <label className="label-brutal">Comissão % (paga)</label>
          <input
            data-testid="contractor-commission"
            className="input-brutal"
            placeholder="Ex: 30 · em cima do recebido"
            value={form.commission_pct}
            onChange={(e) => set("commission_pct", e.target.value)}
          />
        </div>
        <div className="md:col-span-2 lg:col-span-3 flex gap-2 justify-end">
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(EMPTY); }} className="btn-white">
              <X weight="bold" size={16} /> Cancelar
            </button>
          )}
          <button data-testid="save-contractor-btn" type="submit" className="btn-pink">
            {editing ? <><FloppyDisk weight="bold" size={16} />Salvar</> : <><Plus weight="bold" size={16} />Adicionar</>}
          </button>
        </div>
      </form>

      {items.length === 0 ? (
        <div className="card-brutal p-10 text-center">
          <Handshake weight="bold" size={40} className="mx-auto" />
          <p className="font-heading text-xl font-black mt-3">Nenhum contratante cadastrado</p>
          <p className="text-brand-ink2 text-sm">Cadastre parceiros que contratam suas lives.</p>
        </div>
      ) : (
        <div className="card-brutal overflow-hidden">
          <table className="tbl w-full">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Telefone</th>
                <th>Acesso do contratante</th>
                <th className="w-56 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} data-testid={`contractor-row-${c.id}`}>
                  <td className="font-bold">{c.name}</td>
                  <td>{c.phone || "—"}</td>
                  <td>
                    {c.access_email ? (
                      <div className="flex items-center gap-2">
                        <span className="badge-brutal bg-brand-mint text-brand-ink">
                          <Envelope weight="bold" size={12} /> {c.access_email}
                        </span>
                        {c.access_status === "pending" && (
                          <span className="badge-brutal bg-brand-yellow text-brand-ink">Pendente</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-brand-ink2 text-sm">Sem acesso</span>
                    )}
                  </td>
                  <td className="text-right">
                    <div className="inline-flex gap-1">
                      <button
                        data-testid={`contractor-report-${c.id}`}
                        onClick={() => nav(`/reports/contractors?contractor_id=${c.id}`)}
                        className="p-2 border-2 border-brand-ink bg-white hover:bg-brand-mint"
                        title="Ver relatório"
                      >
                        <ChartBar weight="bold" size={14} />
                      </button>
                      {isMaster && (
                        <button
                          data-testid={`contractor-view-as-${c.id}`}
                          onClick={() => startViewAs(c)}
                          className="p-2 border-2 border-brand-ink bg-white hover:bg-brand-yellow"
                          title="Ver como esta contratante"
                        >
                          <Eye weight="bold" size={14} />
                        </button>
                      )}
                      {isMaster && (
                        <button
                          data-testid={`contractor-access-${c.id}`}
                          onClick={() => setAccessFor(c)}
                          className="p-2 border-2 border-brand-ink bg-white hover:bg-brand-yellow"
                          title="Gerenciar acesso"
                        >
                          <UserCircleGear weight="bold" size={14} />
                        </button>
                      )}
                      <button data-testid={`edit-contractor-${c.id}`} onClick={() => startEdit(c)} className="p-2 border-2 border-brand-ink bg-white hover:bg-brand-yellow">
                        <PencilSimple weight="bold" size={14} />
                      </button>
                      <button data-testid={`delete-contractor-${c.id}`} onClick={() => remove(c.id)} className="p-2 border-2 border-brand-ink bg-white hover:bg-brand-peach">
                        <Trash weight="bold" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {accessFor && (
        <ContractorAccessModal
          contractor={accessFor}
          onClose={() => setAccessFor(null)}
          onSaved={() => { setAccessFor(null); load(); }}
        />
      )}
    </div>
  );
}

function ContractorAccessModal({ contractor, onClose, onSaved }) {
  const [email, setEmail] = useState(contractor.access_email || "");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) return toast.error("Informe um e-mail válido");
    setSaving(true);
    try {
      await api.post(`/contractors/${contractor.id}/access`, { email: email.trim().toLowerCase(), name: name.trim() || null });
      toast.success("Acesso vinculado. O contratante já pode entrar com este e-mail.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao vincular acesso");
    } finally { setSaving(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Remover o acesso deste contratante? Ele não poderá mais entrar.")) return;
    setSaving(true);
    try {
      await api.delete(`/contractors/${contractor.id}/access`);
      toast.success("Acesso removido");
      onSaved();
    } catch { toast.error("Erro ao remover acesso"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-brand-ink/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="contractor-access-modal" className="card-brutal w-full max-w-lg bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs font-bold uppercase text-brand-ink2">Acesso do contratante</p>
            <p className="font-heading text-2xl font-black">{contractor.name}</p>
          </div>
          <button onClick={onClose} className="p-2 border-2 border-brand-ink"><X weight="bold" size={16} /></button>
        </div>
        <p className="text-sm text-brand-ink2 mb-4">
          Vincule um e-mail Google. Ao fazer login com esse e-mail, o contratante verá <strong>apenas as próprias lives</strong> e o relatório de prestação de contas.
        </p>
        <form onSubmit={save} className="space-y-3">
          <div>
            <label className="label-brutal">E-mail Google *</label>
            <input
              data-testid="contractor-access-email-input"
              autoFocus
              type="email"
              className="input-brutal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contratante@gmail.com"
            />
          </div>
          <div>
            <label className="label-brutal">Nome (opcional)</label>
            <input
              data-testid="contractor-access-name-input"
              className="input-brutal"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome de exibição"
            />
          </div>
          <div className="flex justify-between gap-2 pt-2">
            {contractor.access_email ? (
              <button
                type="button"
                data-testid="contractor-access-revoke-btn"
                onClick={revoke}
                disabled={saving}
                className="btn-white text-brand-pink"
              >
                <Trash weight="bold" size={16} /> Remover acesso
              </button>
            ) : <span />}
            <button data-testid="contractor-access-save-btn" type="submit" disabled={saving} className="btn-pink">
              <FloppyDisk weight="bold" size={16} /> Salvar acesso
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
